<template>
    <div
        class="bg-bg relative flex flex-col"
        :style="{ paddingBottom: 'calc(1rem + var(--safezone-bottom, 0px))' }"
    >
        <!-- While input is locked (e.g. the bot is throwing), dim the grid with an
             overlay that sits below the Re-Do button (z-30) so the player can still
             tap Re-Do to edit their last turn. Editing clears `disabled` and the overlay. -->
        <div v-if="disabledAndNotEditing" class="absolute inset-0 z-20 bg-bg/60"></div>

        <CheckoutDartsModal
            :is-visible="showCheckoutModal"
            :available-dart-counts="availableDartCounts"
            :available-double-attempts="availableCheckoutDoubleAttempts"
            :is-submitting="isSubmitting"
            @select="handleCheckoutDartSelect"
            @cancel="handleCheckoutModalCancel"
        />

        <CheckoutAttemptsModal
            :is-visible="showCheckoutAttemptsModal"
            :available-attempts="availableDoubleAttempts"
            :is-submitting="isSubmitting"
            @select="handleCheckoutAttemptsSelect"
            @cancel="handleCheckoutAttemptsCancel"
        />

        <div class="flex flex-col">
            <div class="relative flex items-center gap-4 p-2">
                <GridToggleButton
                    mode="total"
                    :disabled="disableGridToggle"
                    @toggle="$emit('toggle-grid')"
                />

                <div class="relative flex-1">
                    <div
                        class="bg-card border-stroke-1 flex h-9 items-center justify-center border-1 px-4"
                        data-testid="score-display"
                        :class="{ 'border-red-500': showValidationError && validationMessage }"
                    >
                        <span
                            v-if="currentScore !== null"
                            class="font-title text-xl font-semibold text-white"
                        >
                            {{ currentScore }}
                        </span>
                        <span v-else class="text-titles w-full text-center">
                            {{ $t('score_grid.enter_your_score') }}
                        </span>
                    </div>
                </div>

                <button
                    @click="submitTurn"
                    :disabled="disabledAndNotEditing || currentScore === null || isSubmitting"
                    data-testid="confirm-turn-button"
                    class="font-title clip-left-corner-small relative h-9 bg-red-600 px-4 !text-xs font-semibold tracking-tight text-white uppercase transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <span
                        v-if="isSubmitting"
                        class="absolute inset-0 flex items-center justify-center bg-red-600"
                    >
                        <svg
                            class="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                    </span>
                    <span :class="{ 'opacity-0': isSubmitting }">{{ $t('general.confirm') }}</span>
                </button>

                <div
                    v-if="showValidationError && validationMessage"
                    data-testid="score-validation"
                    class="absolute -top-9 right-0 left-0 z-10 bg-red-900/95 px-4 py-2.5 text-center text-xs font-semibold text-red-200"
                >
                    {{ validationMessage }}
                </div>
            </div>

            <div class="border-stroke-1 divide-stroke-1 grid grid-cols-3 divide-x border-t">
                <button
                    v-for="num in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
                    :key="num"
                    :disabled="disabledAndNotEditing || isSubmitting"
                    @click="addDigit(num)"
                    class="border-stroke-1 bg-bg hover:bg-card flex h-15 items-center justify-center border-b font-bold text-white transition-colors"
                >
                    <div class="text-3xl">{{ num }}</div>
                </button>
            </div>

            <div
                class="border-stroke-1 divide-stroke-1 grid grid-cols-3 items-start divide-x border-b"
            >
                <button
                    :disabled="(!matchStore.editingTurn && !hasLastTurn) || isSubmitting"
                    @click="matchStore.editingTurn ? cancelEditing() : loadLastTurn()"
                    data-testid="redo-turn-button"
                    class="relative z-30 bg-bg hover:bg-card flex h-15 min-w-0 items-center justify-center px-2 text-sm font-extrabold text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <svg
                        v-if="!matchStore.editingTurn"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="mr-1 size-5 shrink-0"
                    >
                        <path
                            d="M20.7002 13.5369C20.2995 15.507 19.3111 17.2978 17.8701 18.6395C16.4281 19.982 14.6091 20.8029 12.6797 20.9686C10.7499 21.1343 8.82611 20.6346 7.19434 19.5516C6.35293 18.9931 5.61436 18.2945 5 17.492V19.8377H3V13.6102H8.91895V15.6102H6.13184C6.68846 16.5283 7.4281 17.3059 8.2998 17.8846C9.56226 18.7225 11.0378 19.1027 12.5088 18.9764C13.98 18.85 15.3831 18.2228 16.5078 17.1756C17.6335 16.1275 18.4199 14.713 18.7402 13.1375L20.7002 13.5369ZM11.3213 3.03303C13.2506 2.86736 15.1741 3.36761 16.8057 4.45002C17.6306 4.99733 18.3576 5.67827 18.9648 6.46076V3.99982H20.9648V4.16291H21V10.3904H20.9648V10.4012H14.8848V8.40119H15.085V8.39045H17.8682C17.3113 7.4725 16.572 6.69446 15.7002 6.11603C14.438 5.27863 12.9627 4.89991 11.4922 5.02619C10.0214 5.15254 8.6187 5.77933 7.49414 6.82599C6.36879 7.87351 5.58161 9.28653 5.26074 10.8612L3.30078 10.4627C3.70201 8.49325 4.69103 6.7032 6.13184 5.36213C7.57371 4.02013 9.39234 3.19867 11.3213 3.03303Z"
                            fill="white"
                        />
                    </svg>
                    <svg
                        v-else
                        class="mr-1 size-5 shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                    </svg>
                    <span class="min-w-0 truncate">{{ matchStore.editingTurn ? $t('general.cancel') : $t('score_grid.redo') }}</span>
                </button>
                <button
                    :disabled="disabledAndNotEditing || isSubmitting"
                    @click="addDigit(0)"
                    class="bg-bg hover:bg-card flex h-15 items-center justify-center font-bold text-white transition-colors"
                >
                    <div class="text-3xl">0</div>
                </button>
                <button
                    :disabled="disabled || isSubmitting || currentScore === null"
                    @click="clearScore"
                    data-testid="clear-score-button"
                    class="bg-bg hover:bg-card flex h-15 items-center justify-center text-sm font-extrabold text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {{ $t('score_grid.clear') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { apiService } from '@/services/api.js'
import GridToggleButton from '@/components/ui/GridToggleButton.vue'
import CheckoutDartsModal from '@/components/CheckoutDartsModal.vue'
import CheckoutAttemptsModal from '@/components/CheckoutAttemptsModal.vue'
import { useDartScoring } from '@/composables/useDartScoring.js'
import checkoutService from '@/services/checkout.js'
import {
    STRAIGHT_OUT_MIN_DARTS,
    DOUBLE_OUT_MIN_DARTS,
    MASTER_OUT_MIN_DARTS,
} from '@/constants/checkoutTable.js'
import doubleAttemptsCalculator from '@/services/doubleAttempts.js'

const ONE_DART_SCORES = (() => {
    const scores = new Set([0])
    for (let n = 1; n <= 20; n++) scores.add(n)
    scores.add(25)
    scores.add(50)
    for (let n = 1; n <= 20; n++) scores.add(2 * n)
    for (let n = 1; n <= 20; n++) scores.add(3 * n)
    return Array.from(scores)
})()

const THREE_DART_TOTALS = (() => {
    const totals = new Set()
    const vals = ONE_DART_SCORES
    for (let i = 0; i < vals.length; i++) {
        for (let j = 0; j < vals.length; j++) {
            for (let k = 0; k < vals.length; k++) {
                const t = vals[i] + vals[j] + vals[k]
                if (t <= 180) totals.add(t)
            }
        }
    }
    return totals
})()

const THREE_DART_TOTALS_WITH_DOUBLE_OR_TRIPLE = (() => {
    const totals = new Set()
    const vals = ONE_DART_SCORES

    const isDoubleOrTriple = (val) => {
        if (val === 50) return true
        if (val === 0 || val === 25) return false
        if (val >= 2 && val <= 40 && val % 2 === 0) return true
        if (val >= 3 && val <= 60 && val % 3 === 0) return true
        return false
    }

    for (let i = 0; i < vals.length; i++) {
        for (let j = 0; j < vals.length; j++) {
            for (let k = 0; k < vals.length; k++) {
                const t = vals[i] + vals[j] + vals[k]
                if (
                    t <= 180 &&
                    (isDoubleOrTriple(vals[i]) ||
                        isDoubleOrTriple(vals[j]) ||
                        isDoubleOrTriple(vals[k]))
                ) {
                    totals.add(t)
                }
            }
        }
    }

    return totals
})()

const THREE_DART_TOTALS_WITH_DOUBLE_IN = (() => {
    const totals = new Set()
    const vals = ONE_DART_SCORES

    const isDouble = (val) => {
        if (val === 50) return true // Bull is a double
        if (val >= 2 && val <= 40 && val % 2 === 0) return true
        return false
    }

    // For double-in, only darts AFTER the first double count
    // Pattern 1: Double first (all 3 darts count)
    for (let i = 0; i < vals.length; i++) {
        if (!isDouble(vals[i])) continue
        for (let j = 0; j < vals.length; j++) {
            for (let k = 0; k < vals.length; k++) {
                totals.add(vals[i] + vals[j] + vals[k])
            }
        }
    }

    // Pattern 2: Miss/single/triple, then double (only last 2 darts count)
    for (let i = 0; i < vals.length; i++) {
        if (isDouble(vals[i])) continue
        for (let j = 0; j < vals.length; j++) {
            if (!isDouble(vals[j])) continue
            for (let k = 0; k < vals.length; k++) {
                totals.add(vals[j] + vals[k])
            }
        }
    }

    // Pattern 3: Miss/single/triple, miss/single/triple, then double (only last dart counts)
    for (let i = 0; i < vals.length; i++) {
        if (isDouble(vals[i])) continue
        for (let j = 0; j < vals.length; j++) {
            if (isDouble(vals[j])) continue
            for (let k = 0; k < vals.length; k++) {
                if (!isDouble(vals[k])) continue
                totals.add(vals[k])
            }
        }
    }

    return totals
})()

export default {
    name: 'TotalScoreGrid',
    components: { GridToggleButton, CheckoutDartsModal, CheckoutAttemptsModal },
    props: {
        currentPlayer: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disableGridToggle: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle-grid'],
    data() {
        return {
            currentScore: null,
            isSubmitting: false,
            showValidationError: false,
            showCheckoutModal: false,
            showCheckoutAttemptsModal: false,
        }
    },
    watch: {
        'matchStore.editingTurn'(newEditingTurn) {
            if (newEditingTurn && newEditingTurn.thrown_score !== undefined) {
                this.clearScore()
                this.currentScore = null
            }
        },
    },
    setup(props) {
        const {
            matchStore,
            settings,
            checkInType,
            checkOutType,
            requiresDoubleIn,
            requiresDoubleOrTripleIn,
            requiresSpecialOut,
            isFirstTurnInLeg,
            maxScore,
            remainingScore,
            canCheckout,
            effectiveCheckOutType,
            effectiveRemainingBefore,
            hasLastTurn,
            loadLastTurn,
            submitDartData,
        } = useDartScoring(computed(() => props.currentPlayer))

        return {
            matchStore,
            settings,
            checkInType,
            checkOutType,
            requiresSpecialOut,
            requiresDoubleIn,
            requiresDoubleOrTripleIn,
            isFirstTurnInLeg,
            maxScore,
            remainingScore,
            canCheckout,
            effectiveCheckOutType,
            effectiveRemainingBefore,
            hasLastTurn,
            loadTurnForEditing: loadLastTurn,
            submitDartData,
        }
    },
    computed: {
        maxInputScore() {
            return 999
        },
        disabledAndNotEditing() {
            return this.disabled && this.matchStore.editingTurn === null
        },
        isValidTurn() {
            if (this.currentScore === null) return false

            const score = this.currentScore

            if (score < 0 || score > this.maxScore) return false

            if (this.isFirstTurnInLeg && this.requiresDoubleOrTripleIn) {
                return THREE_DART_TOTALS_WITH_DOUBLE_OR_TRIPLE.has(score)
            }

            if (this.isFirstTurnInLeg && this.requiresDoubleIn && !this.requiresDoubleOrTripleIn) {
                return THREE_DART_TOTALS_WITH_DOUBLE_IN.has(score)
            }

            if (!THREE_DART_TOTALS.has(score)) return false
            if (this.wouldBeImpossibleCheckout) return false

            return true
        },
        wouldBeImpossibleCheckout() {
            if (this.currentScore === null) return false
            const isX01Game = this.matchStore.match.matchtype_id === 1
            if (!isX01Game) return false

            const remainingAfter = this.effectiveRemainingAfter
            if (remainingAfter !== 0) return false

            const score = this.currentScore
            if (score < 1 || score > 180) return true

            let minDartsTable
            if (this.effectiveCheckOutType === 'master') {
                minDartsTable = MASTER_OUT_MIN_DARTS
            } else if (this.effectiveCheckOutType === 'double') {
                minDartsTable = DOUBLE_OUT_MIN_DARTS
            } else {
                minDartsTable = STRAIGHT_OUT_MIN_DARTS
            }

            return minDartsTable[score] === null
        },
        validationMessage() {
            if (this.currentScore === null) return ''

            const score = this.currentScore

            if (this.isFirstTurnInLeg && this.requiresDoubleOrTripleIn) {
                if (score > this.maxScore) {
                    return this.$t('score_grid.score_range_error', { max: this.maxScore })
                }
                if (!THREE_DART_TOTALS_WITH_DOUBLE_OR_TRIPLE.has(score)) {
                    return this.$t('score_grid.double_or_triple_in_required')
                }
            }

            if (this.isFirstTurnInLeg && this.requiresDoubleIn && !this.requiresDoubleOrTripleIn) {
                if (score > this.maxScore) {
                    return this.$t('score_grid.score_range_error', { max: this.maxScore })
                }
                if (!THREE_DART_TOTALS_WITH_DOUBLE_IN.has(score)) {
                    return this.$t('score_grid.double_in_required')
                }
            }

            if (score < 0 || score > this.maxScore) {
                return this.$t('score_grid.score_range_error', { max: this.maxScore })
            }

            if (!THREE_DART_TOTALS.has(score)) {
                return this.$t('score_grid.impossible_score')
            }

            if (this.wouldBeImpossibleCheckout) {
                return this.$t('score_grid.invalid_checkout', { score })
            }

            return ''
        },
        effectiveRemainingAfter() {
            if (this.currentScore === null || this.effectiveRemainingBefore === null) return null
            return this.effectiveRemainingBefore - this.currentScore
        },
        isCheckoutScenario() {
            if (this.currentScore === null) return false
            const isX01Game = this.matchStore.match.matchtype_id === 1

            if (this.effectiveRemainingBefore === null) return false

            const newRemaining = this.effectiveRemainingAfter
            return newRemaining === 0 && isX01Game
        },
        isFinishAttempt() {
            if (this.currentScore === null) return false
            const isX01Game = this.matchStore.match.matchtype_id === 1

            if (!isX01Game) return false
            if (this.effectiveRemainingBefore === null) return false

            // A checkout (remaining -> 0) is handled by the checkout flow, not here.
            if (this.effectiveRemainingAfter === 0) return false

            // It's a finish attempt if at least one dart could have been thrown at
            // a checkout this turn. The engine works this out per checkout type,
            // so this stays correct for double-, master- and straight-out.
            return doubleAttemptsCalculator
                .computePossibleAttempts(
                    this.effectiveRemainingBefore,
                    this.currentScore,
                    this.effectiveCheckOutType,
                )
                .some((count) => count >= 1)
        },
        availableDartCounts() {
            if (!this.isCheckoutScenario) return []
            return checkoutService.getPossibleDartCounts(this.currentScore)
        },
        availableDoubleAttempts() {
            if (
                !this.isFinishAttempt ||
                this.currentScore === null ||
                this.effectiveRemainingBefore === null
            ) {
                return [0, 1, 2, 3]
            }

            const options = doubleAttemptsCalculator.computePossibleAttempts(
                this.effectiveRemainingBefore,
                this.currentScore,
                this.effectiveCheckOutType,
            )

            return options.length > 0 ? options : [0, 1, 2, 3]
        },
        availableCheckoutDoubleAttempts() {
            if (
                !this.isCheckoutScenario ||
                this.currentScore === null ||
                this.effectiveRemainingBefore === null
            ) {
                return [1, 2, 3]
            }

            // A successful checkout means the double was hit, so at least one
            // dart was thrown at a double — drop the 0 option the engine only
            // produces for non-finishing turns.
            const finishAttempts = doubleAttemptsCalculator
                .computePossibleAttempts(
                    this.effectiveRemainingBefore,
                    this.currentScore,
                    this.effectiveCheckOutType,
                )
                .filter((count) => count >= 1)

            return finishAttempts.length > 0 ? finishAttempts : [1, 2, 3]
        },
    },
    methods: {
        addDigit(digit) {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            this.showValidationError = false
            const currentStr = this.currentScore?.toString() || ''

            if (currentStr.length >= 3) return

            const newScore = parseInt(currentStr + digit.toString())

            if (newScore <= this.maxInputScore) {
                this.currentScore = newScore
            }
        },
        removeLastDigit() {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            if (!this.currentScore) return
            this.showValidationError = false

            const currentStr = this.currentScore.toString()
            if (currentStr.length === 1) {
                this.currentScore = null
            } else {
                this.currentScore = parseInt(currentStr.slice(0, -1))
            }
        },
        clearScore() {
            if (this.disabledAndNotEditing || this.isSubmitting) return
            this.showValidationError = false
            this.currentScore = null
        },
        getCurrentScore() {
            return this.currentScore
        },
        resetScore() {
            this.currentScore = null
            this.showValidationError = false
        },
        loadLastTurn() {
            this.loadTurnForEditing('score_grid.editing_hint_total')
        },
        cancelEditing() {
            this.matchStore.clearEditingTurn()
            this.clearScore()
        },
        async submitTurn() {
            if (this.disabledAndNotEditing) return
            if (this.currentScore === null) {
                return
            }
            if (!this.isValidTurn) {
                this.showValidationError = true
                return
            }

            if (!this.matchStore.match.id) {
                console.error('No active match to submit dart to')
                return
            }

            if (this.isCheckoutScenario && this.availableDartCounts.length > 0) {
                // Skip the modal only when both the number of darts used and the
                // number of darts at a double are certain (true for finishes that
                // can only be done in three darts). Otherwise ask, with both option
                // sets already narrowed to what's actually possible.
                const dartCounts = this.availableDartCounts
                const attempts = this.availableCheckoutDoubleAttempts

                if (dartCounts.length === 1 && attempts.length === 1) {
                    await this.submitWithDartCount(dartCounts[0], attempts[0])
                } else {
                    this.showCheckoutModal = true
                }
                return
            }

            if (this.isFinishAttempt) {
                // Work out every double-attempt count consistent with this turn's
                // total. When only one is possible the answer is certain, so record
                // it and skip the modal; otherwise ask, but only for the counts that
                // are actually possible.
                const { value } = doubleAttemptsCalculator.resolveAttempts(
                    this.effectiveRemainingBefore,
                    this.currentScore,
                    this.effectiveCheckOutType,
                )

                if (value !== null) {
                    await this.submitWithDartCount(3, value)
                    return
                }

                this.showCheckoutAttemptsModal = true
                return
            }

            this.isSubmitting = true

            try {
                if (this.matchStore.editingTurn) {
                    await this.updateTurn()
                } else {
                    const payload = {
                        action: 'submit_turn',
                        match_id: this.matchStore.match.id,
                        profile_id: this.currentPlayer.id,
                        dart: {
                            score: this.currentScore,
                        },
                        halo_macaddress: null,
                    }

                    const response = await apiService.post('/api/v1/dart-data', payload)

                    if (response?.rt_matchdata || response?.rt_gatekeeper) {
                        this.matchStore.applyAuthoritativeRtMatchSnapshot(response)
                    }
                }

                this.resetScore()
                this.matchStore.clearEditingTurn()
            } catch (error) {
                console.error('Failed to submit total score:', error)
            } finally {
                this.isSubmitting = false
            }
        },
        async submitWithDartCount(dartCount, doubleAttempts = null) {
            this.isSubmitting = true
            this.showCheckoutModal = false
            this.showCheckoutAttemptsModal = false

            try {
                if (this.matchStore.editingTurn) {
                    await this.updateTurn(dartCount, doubleAttempts)
                } else {
                    const dartPayload = {
                        score: this.currentScore,
                        darts_thrown: dartCount,
                    }

                    if (doubleAttempts !== null) {
                        dartPayload.double_attempts = doubleAttempts
                    }

                    const payload = {
                        action: 'submit_turn',
                        match_id: this.matchStore.match.id,
                        profile_id: this.currentPlayer.id,
                        dart: dartPayload,
                        halo_macaddress: null,
                    }

                    const response = await apiService.post('/api/v1/dart-data', payload)

                    if (response?.rt_matchdata || response?.rt_gatekeeper) {
                        this.matchStore.applyAuthoritativeRtMatchSnapshot(response)
                    }
                }

                this.resetScore()
                this.matchStore.clearEditingTurn()
            } catch (error) {
                console.error('Failed to submit total score:', error)
            } finally {
                this.isSubmitting = false
            }
        },
        handleCheckoutDartSelect({ dartCount, doubleAttempts }) {
            this.submitWithDartCount(dartCount, doubleAttempts)
        },
        handleCheckoutModalCancel() {
            this.showCheckoutModal = false
            this.isSubmitting = false
        },
        handleCheckoutAttemptsSelect(doubleAttempts) {
            this.submitWithDartCount(3, doubleAttempts)
        },
        handleCheckoutAttemptsCancel() {
            this.showCheckoutAttemptsModal = false
            this.isSubmitting = false
        },
        async updateTurn(dartCount = null, doubleAttempts = null) {
            const dartPayload = {
                score: this.currentScore,
            }

            if (dartCount !== null) {
                dartPayload.darts_thrown = dartCount
            }

            if (doubleAttempts !== null) {
                dartPayload.double_attempts = doubleAttempts
            }

            const payload = {
                action: 'update_turn',
                match_id: this.matchStore.match.id,
                profile_id: this.matchStore.editingTurn.profile_id,
                set_index: this.matchStore.editingTurn.set_index,
                leg_index: this.matchStore.editingTurn.leg_index,
                turn_index: this.matchStore.editingTurn.turn_index,
                dart: dartPayload,
                halo_macaddress: null,
            }

            const response = await apiService.post('/api/v1/dart-data', payload)

            if (response?.rt_matchdata || response?.rt_gatekeeper) {
                this.matchStore.applyAuthoritativeRtMatchSnapshot(response)
            }

            return response
        },
    },
}
</script>
